import { useState } from "react";
import Layout from "../../../Containers/Layout";
import styles from "./ReportForm.module.scss";
import { Form, DatePicker, Col, Row, Upload, Button, UploadProps, UploadFile, Tooltip } from "antd";
import { useParams, Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { RecommendationDTO, ReportType, StudyCycleEnum } from "../../../Api";
import { getReportById, updateReport, addReportWithFile } from "../../../Requests/base-report-requests";
import { dateFormat } from "../../../utils/constants";
import locale from "antd/es/date-picker/locale/ro_RO";
import moment, { Moment } from "moment";
import { NOTIFICATION_TYPES, openNotification } from "../../Notifications/NotificationsUtils";
import { InfoCircleOutlined, UploadOutlined } from "@ant-design/icons";
import { RcFile } from "antd/lib/upload";
import { theme } from "../../../theme";
import CustomForm from "../../../CustomComponents/CustomForm";
import ConfirmationModal from "../../../Containers/ConfirmationModal";
import RecommendationForm from "./RecommendationForm";

const ReportForm = () => {
    const { t } = useTranslation();
    const [redirect, setRedirect] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form] = Form.useForm<{
        reportDate: Moment[];
        reportSimpleDate: Moment;
        description: string;
        recommendations: RecommendationDTO[];
        file: any;
    }>();

    const { reportId, programId, studyCycle, reportType } = useParams<{
        reportId: string;
        programId: string;
        studyCycle: StudyCycleEnum;
        reportType: string;
    }>();

    const { data: report } = useQuery(
        ["getReport", reportId],
        () => getReportById(reportId),
        {
            onError: () => {
                openNotification(
                    "Eroare",
                    "Datele nu au putut fi aduse din server!",
                    NOTIFICATION_TYPES.ERROR
                );
            },
            onSuccess: (data) => {
                if (data) {
                    form.setFieldsValue({
                        reportDate: [moment(data.startDate), moment(data.reportDate)],
                        reportSimpleDate: moment(data.reportDate),
                        recommendations: data.recommendations ?? [],
                        description: data.description ?? "",
                    });
                }
            },
            enabled: !!reportId,
            refetchOnWindowFocus: false,
        }
    );

    const handleCancel = () => {
        setRedirect(true);
    };

    const handleUpdate = () => {
        if (isSubmitting) {
            return;
        }

        const state = form.getFieldsValue();

        const recommendations = state.recommendations.map((rec: any) => ({
            id: rec.id || undefined,
            description: rec.description,
            officialName: rec.officialName,
            state: rec.state,
            externalEvaluationId: rec.externalEvaluationId || undefined,
        }));

        const reportData = {
            reportDate: reportType === ReportType.ExternalEvaluation ? moment(state.reportDate[1]).toDate() : moment(state.reportSimpleDate).toDate(),
            startDate: reportType === ReportType.ExternalEvaluation ? moment(state.reportDate[0]).toDate() : undefined,
            description: state.description,
            recommendations: reportType === ReportType.ExternalEvaluation ? recommendations : undefined,
            reportType: reportType as ReportType,
        };


        setIsSubmitting(true);

         updateReport(reportId, reportData)
            .then(() => {
                openNotification(
                    "Succes!",
                    "Salvarea s-a efectuat cu succes!",
                    NOTIFICATION_TYPES.SUCCESS
                );
                setRedirect(true);
            })
            .catch((error) => {
                setIsSubmitting(false);
                console.error("Error:", error.response?.data || error.message);
                openNotification(
                    "Eroare!",
                    "Datele completate nu sunt valide sau salvarea a eșuat!",
                    NOTIFICATION_TYPES.ERROR
                );
            });
    };

    const handleSaveWithFile = () => {
        if (isSubmitting) {
            return;
        }

        const state = form.getFieldsValue();
        var recommendations: RecommendationDTO[] = [];
        if(reportType === ReportType.ExternalEvaluation) {
             recommendations = state.recommendations.map((rec: any) => ({
                id: rec.id || undefined,
                description: rec.description,
                officialName: rec.officialName,
                state: rec.state,
                externalEvaluationId: rec.externalEvaluationId || undefined,
            }));
        }
        const reportData = {
            reportDate: reportType === ReportType.ExternalEvaluation ? moment(state.reportDate[1]).toDate().toISOString() as any : moment(state.reportSimpleDate).toDate().toISOString() as any,
            startDate: reportType === ReportType.ExternalEvaluation ? moment(state.reportDate[0]).toDate().toISOString() as any : undefined,
            description: state.description,
            recommendationsJson: reportType === ReportType.ExternalEvaluation
                                ? JSON.stringify(recommendations)
                                : undefined,
            reportType: reportType as ReportType,
            file: fileList[0] as RcFile,
            filePath: fileList[0].originFileObj?.name,
            studyProgramId: programId,
        };

        setIsSubmitting(true);

        addReportWithFile(reportData)
        .then(() => {
            openNotification(
                "Succes!",
                "Salvarea s-a efectuat cu succes!",
                NOTIFICATION_TYPES.SUCCESS
            );
            setRedirect(true);
        })
        .catch((error) => {
            setIsSubmitting(false);
            console.error("Error:", error.response?.data || error.message);
            openNotification(
                "Eroare!",
                "Datele completate nu sunt valide sau salvarea a eșuat!",
                NOTIFICATION_TYPES.ERROR
            );
        });
    };

    const { RangePicker } = DatePicker;

    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const uploadProps: UploadProps = {
        onRemove: (file) => {
            const index = fileList.indexOf(file);
            const newFileList = fileList.slice();
            newFileList.splice(index, 1);
            setFileList(newFileList);
        },
        beforeUpload: (file) => {
            setFileList([file]);
            return false;
        },
        fileList,
    };

    if (redirect) {
        return <Redirect to={`/${studyCycle}/${reportType}/${programId}`} />;
    }

    return (
            <Layout>
            <div className={styles.container}>
            <CustomForm
                form={form}
                layout={theme.layout}
                action="/domenii"
                boxshadow={"none"}
            >
                <div className={styles.firstLine}>
                <Row gutter={2}>
                    <Col xs={24} sm={24} md={24} lg={!reportId ? 12 : 24} xl={!reportId ? 12 : 24}>
                    { reportType == ReportType.ExternalEvaluation
                    ?
                        <Form.Item
                            required
                            name='reportDate'
                            initialValue={[moment(), moment().add(moment.duration(30, 'days'))]}
                            label='Data evaluare:'
                            rules={[
                            {
                                required: true,
                                message: 'Data evaluării este obligatorie!',
                                type: 'array'
                            },
                            ]}
                            >
                            <RangePicker
                            locale={locale}
                            format={dateFormat}
                            style={{ width: "100%", height: "35px" }}
                            />
                        </Form.Item>
                    :
                        <Form.Item
                            required
                            name='reportSimpleDate'
                            label='Data raport:'
                            initialValue={moment()}
                            rules={[
                            {
                                required: true,
                                message: 'Data raportului este obligatorie!',
                                type: 'array'
                            },
                            ]}
                            >
                            <DatePicker
                            disabled
                            locale={locale}
                            format={dateFormat}
                            style={{ width: "100%", height: "35px" }}
                            />
                        </Form.Item>
                    }
                    </Col>

                    {!reportId && <Col xs={24} sm={24} md={24} lg={12} xl={12}>
                    <Form.Item
                        name='file'
                        label='Fisier (PDF):'
                        rules={[
                        {
                            required: true,
                            validator: (_, value) => {
                            if (value.file.type === 'application/pdf') {
                                return Promise.resolve();
                            } else if (value.fileList.length === 0) {
                                return Promise.reject('Documentul este obligatoriu!');
                            } else {
                                return Promise.reject('Fișierul încărcat nu este PDF!');
                            }
                            }
                        },
                        ]}
                        >
                        <Upload {...uploadProps} accept=".pdf" multiple={false} maxCount={1}>
                        <Button type="primary" icon={<UploadOutlined />}>Alege fișierul</Button>
                        </Upload>
                    </Form.Item>
                    </Col>}

                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Form.Item
                        name='description'
                        label={
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            Descriere:
                            {reportType === ReportType.InternalEvaluationReport && (
                                <Tooltip title="Descriere proces validare la nivelul facultatii" trigger={["hover", "click"]}>
                                <InfoCircleOutlined style={{ color: "#1890ff", cursor: "pointer" }} />
                                </Tooltip>
                            )}
                            </span>
                        }
                        rules={[
                        {
                            required: true,
                            message: 'Descrierea este obligatorie!',
                            whitespace: true,
                        },
                        ]}
                        >
                        <textarea
                        rows={3}
                        cols={100}
                        placeholder='Completează descrierea evaluării'
                        />
                    </Form.Item>
                    </Col>


                    {reportType === ReportType.ExternalEvaluation && (
                        <Col span={24}>
                            <RecommendationForm
                            name="recommendations"
                            initialValue={[{}]}
                            isAdd={reportId == null}
                            />
                        </Col>
                        )}
                </Row>
                </div>
                <div className={styles.secondLine}>
                <div className={styles.firstColumn}>
                    <ConfirmationModal
                    nameButton={t('internships.addInternshipForm.cancel')}
                    modalText={t('internships.cancelMessage')}
                    titleButton={''}
                    handleFunction={handleCancel}
                    iconButton={null}
                    shapeButton={''}
                    typeButton={''}
                    disabledProp={false}
                    styleProp={{ background: theme.green, color: theme.white, marginRight: '10%', marginTop: '0%', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px' }}
                    />
                    <ConfirmationModal
                    nameButton={reportId ? t('internships.addInternshipForm.updateButton') : t('internships.addInternshipForm.saveButton')}
                    modalText={t('internships.saveMessage')}
                    titleButton={''}
                    handleFunction={reportId ? handleUpdate : handleSaveWithFile}
                    iconButton={null}
                    shapeButton={''}
                    typeButton={''}
                    disabledProp={isSubmitting}
                    styleProp={{ background: theme.secondColor, color: theme.white, marginRight: '10%', marginTop: '0%', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px' }}
                    />
                </div>
                </div>
            </CustomForm>
            </div>
            </Layout>
    );
};

export default ReportForm;